import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClockIcon, ShieldCheckIcon, TruckIcon, ChevronRightIcon, PhoneIcon, MessageCircleIcon, SmartphoneIcon, UploadIcon, SearchIcon, ArrowRightIcon, CheckIcon, StethoscopeIcon } from 'lucide-react';
export function PrescriptionPageV2() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const benefits = [{
    icon: ClockIcon,
    text: 'Available 24/7',
    sub: 'Always here for you'
  }, {
    icon: ShieldCheckIcon,
    text: 'Quick Insurance Approval',
    sub: 'Hassle-free process'
  }, {
    icon: TruckIcon,
    text: 'Free Home Delivery',
    sub: 'Straight to your door'
  }];
  const steps = [{
    icon: UploadIcon,
    title: 'Upload Prescription',
    desc: 'Take a photo or upload your prescription details'
  }, {
    icon: MessageCircleIcon,
    title: 'Pharmacist Review',
    desc: 'Our licensed pharmacists will verify your order'
  }, {
    icon: TruckIcon,
    title: 'Fast Delivery',
    desc: 'Get your medicines delivered within 2-4 hours'
  }];
  return <HelmetProvider data-id="element-7399" data-mp="cuocpdli6w4a">
      <div className="min-h-screen bg-white" data-id="element-7400" data-mp="7wd04v3tq7id">
        <SEO title="Prescription Services V2 - Fast & Secure Medicine Delivery" data-id="element-7401" data-mp="7xef3anyne5d" />

        <div className="hidden md:block" data-id="element-7402" data-mp="3udvh3tejyc78">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-7403" data-mp="3uctucro6c19o" />
        </div>

        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-7404" data-mp="3pfk63fcitvnf">
          <MobileInternalHeader title="Prescription Services" data-id="element-7405" data-mp="h00h4sivuze6" />

          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Prescription Services'
        }]} title="Prescription Services" showTitle={false} showDescription={false} data-id="element-7406" data-mp="lxa481isuyyu" />

          {/* 1. Marketing Hero */}
          <section className="relative overflow-hidden bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] py-12 md:py-20" data-id="element-7407" data-mp="14putl8zailxw">
            <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none" data-id="element-7408" data-mp="2pdnhgnbp9c57">
              <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" data-id="element-7409" data-mp="2kfm0c7xu3mwd">
                <path fill="#FFFFFF" d="M44.7,-76.4C58.1,-69.2,69.2,-58.1,76.4,-44.7C83.7,-31.3,87.1,-15.7,86.1,-0.6C85.1,14.5,79.7,29,71.4,41.4C63.1,53.8,51.8,64.1,38.7,71.1C25.6,78.1,10.8,81.8,-3.4,87.7C-17.6,93.6,-31.2,101.7,-43.3,99.1C-55.4,96.5,-66,83.2,-73.4,69.2C-80.8,55.2,-85,40.5,-86.3,26.1C-87.6,11.7,-86,2.4,-82.4,-14.9C-78.8,-32.2,-73.2,-57.5,-59.8,-64.7C-46.4,-71.9,-25.2,-61,-4.1,-53.9C17,-46.8,31.3,-83.6,44.7,-76.4Z" transform="translate(100 100)" data-id="element-7410" data-mp="2u5f26h86cnnf" />
              </svg>
            </div>

            <div className="container mx-auto px-4 relative z-10" data-id="element-7411" data-mp="3mum6xxy829h4">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-7412" data-mp="3muchcbh3a4by">
                <div className="flex-1 text-center md:text-left" data-id="element-7413" data-mp="m0za02223zdc">
                  <motion.div initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="inline-block bg-[#FFD700] text-[#1E2B55] text-xs md:text-sm font-black px-4 py-1.5 rounded-full mb-6 uppercase tracking-widest shadow-lg" data-id="element-7414" data-mp="q083r6x3jx8w">
                    Limited Time Offer
                  </motion.div>
                  <motion.h1 initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.1
                }} className="text-4xl md:text-6xl font-black text-white mb-6 leading-[1.1]" data-id="element-7415" data-mp="1drvof73rmc2q">
                    GET UP TO{' '}
                    <span className="text-[#FFD700] text-5xl md:text-7xl block md:inline" data-id="element-7416" data-mp="385ztvass79ze">
                      25%
                    </span>{' '}
                    <br className="hidden md:block" data-id="element-7417" data-mp="2y8ms3bbfhv3p" />
                    VALUE BACK*
                  </motion.h1>
                  <motion.p initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.2
                }} className="text-blue-100 text-lg md:text-xl mb-8 max-w-xl mx-auto md:mx-0 leading-relaxed" data-id="element-7418" data-mp="3vwwtpp7aygua">
                    Upload your prescription now and get the best value back on
                    your medicines. Fastest insurance approval in KSA.
                  </motion.p>
                </div>
                <div className="flex-1 hidden md:block" data-id="element-7419" data-mp="2cxpdzzv06a7p">
                  <motion.img initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.2
                }} src="https://cdn.magicpatterns.com/uploads/d5jJ86NcHDRooittx54KcB/pasted-image.png" alt="Prescription Offer" className="w-full max-w-lg mx-auto drop-shadow-2xl" data-id="element-7420" data-mp="2cx5zyu8h45yy" />
                </div>
              </div>
            </div>
          </section>

          {/* 2. Visual How It Works (Moved up) */}
          <section className="py-10 md:py-20 bg-white relative z-20 -mt-4 md:-mt-6 mx-4 md:mx-auto max-w-6xl rounded-2xl shadow-sm border-b border-gray-100" data-id="element-7421" data-mp="3en19sg5xshte">
            <div className="container mx-auto px-2 md:px-4" data-id="element-7422" data-mp="1307iowe6429e">
              <div className="text-center mb-8 md:mb-16" data-id="element-7423" data-mp="y14ne3nzpaem">
                <h2 className="text-2xl md:text-4xl font-black text-[#1E2B55] mb-3 md:mb-4" data-id="element-7424" data-mp="xyi79fn19mks">
                  How it works?
                </h2>
                <div className="w-16 md:w-24 h-1.5 bg-[#FFD700] mx-auto rounded-full" data-id="element-7425" data-mp="17yisj74gqcrm"></div>
              </div>

              <div className="relative max-w-5xl mx-auto" data-id="element-7426" data-mp="1lp5tl5xp4eqv">
                {/* Connecting Line */}
                <div className="absolute top-8 md:top-12 left-[16.66%] w-[66.66%] h-0.5 bg-gray-200 z-0" data-id="element-7427" data-mp="1gq2ndi5zc2hb"></div>

                <div className="grid grid-cols-3 gap-2 md:gap-12 relative z-10" data-id="element-7428" data-mp="1vn1y3ew4obg0">
                  {steps.map((step, idx) => <div key={idx} className="text-center group flex flex-col items-center" data-id="element-7429" data-mp="1qn7dtu6uyk3x" data-mpi={idx}>
                      <div className="bg-white w-16 h-16 md:w-24 md:h-24 rounded-2xl md:rounded-[32px] shadow-md md:shadow-xl flex items-center justify-center mx-auto mb-3 md:mb-8 border border-gray-50 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 relative" data-id="element-7430" data-mp="3shomxgmajnqn">
                        <div className="absolute -top-2 -right-2 md:-top-3 md:-right-3 w-6 h-6 md:w-8 md:h-8 bg-[#1E2B55] text-white rounded-full flex items-center justify-center font-black text-xs md:text-sm border-2 md:border-4 border-white" data-id="element-7431" data-mp="3gd0yna12teoi">
                          {idx + 1}
                        </div>
                        <step.icon className="w-6 h-6 md:w-10 md:h-10 text-[#1E2B55]" data-id="element-7432" data-mp="2sorfkfv8z512" />
                      </div>
                      <h3 className="text-[11px] md:text-xl font-black text-[#1E2B55] mb-1 md:mb-3 leading-tight px-1" data-id="element-7433" data-mp="2xl859ei3hrg9">
                        {step.title}
                      </h3>
                      <p className="hidden md:block text-gray-500 leading-relaxed px-4" data-id="element-7434" data-mp="cvitjacoulrs">
                        {step.desc}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>
          </section>

          {/* 3. Simplified Decision Making */}
          <section className="py-16 md:py-24 bg-gray-50" data-id="element-7435" data-mp="1xfeg7i2h753h">
            <div className="container mx-auto px-4 max-w-5xl" data-id="element-7436" data-mp="1sn347gugnsuk">
              <div className="text-center mb-12" data-id="element-7437" data-mp="1sljp3tkjjnud">
                <h2 className="text-2xl md:text-4xl font-black text-[#1E2B55] mb-4" data-id="element-7438" data-mp="1nnql976pn5x9">
                  Getting Your Medicines is Easy Now!
                </h2>
                <p className="text-gray-600 text-lg" data-id="element-7439" data-mp="1dqlwr0pe6yda">
                  Send us a picture of your prescription or the medicine you are
                  looking for
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7440" data-mp="14q77xrd7z151">
                <button onClick={() => navigate('/prescription')} className="group relative overflow-hidden bg-white border-2 p-8 rounded-3xl text-left transition-all hover:shadow-2xl flex items-center gap-6 border-transparent shadow-xl hover:border-[#1E2B55]" data-id="element-7441" data-mp="14s0hbef7vzgq">
                  <div className="bg-blue-50 p-5 rounded-2xl group-hover:scale-110 transition-transform" data-id="element-7442" data-mp="bm0qcuxiwizx">
                    <ShieldCheckIcon className="w-10 h-10 text-[#1E2B55]" data-id="element-7443" data-mp="pljkukxevvtr" />
                  </div>
                  <div className="flex-1" data-id="element-7444" data-mp="kmyiofyln22o">
                    <h3 className="text-xl font-black text-[#1E2B55] mb-1" data-id="element-7445" data-mp="ko06ll0prj1t">
                      I have a Valid Prescription
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-7446" data-mp="3p0wx3o36dw4w">
                      Upload Insurance or Non-Insurance Prescriptions
                    </p>
                  </div>
                  <ChevronRightIcon className="w-6 h-6 text-gray-300 group-hover:text-[#1E2B55] group-hover:translate-x-1 transition-all" data-id="element-7447" data-mp="6p0kak0obg8t" />
                </button>

                <button onClick={() => {
                document.getElementById('medical-advice')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }} className="group relative overflow-hidden bg-white border-2 p-8 rounded-3xl text-left transition-all hover:shadow-2xl flex items-center gap-6 border-transparent shadow-xl hover:border-[#1E2B55]" data-id="element-7448" data-mp="3aapri8y9o7lm">
                  <div className="bg-orange-50 p-5 rounded-2xl group-hover:scale-110 transition-transform" data-id="element-7449" data-mp="1gtbfwlrpspsr">
                    <SearchIcon className="w-10 h-10 text-orange-500" data-id="element-7450" data-mp="24j5rl00as8bf" />
                  </div>
                  <div className="flex-1" data-id="element-7451" data-mp="28k7oha6qs8a8">
                    <h3 className="text-xl font-black text-[#1E2B55] mb-1" data-id="element-7452" data-mp="28j6bh0661bpv">
                      I don't have a Prescription
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-7453" data-mp="1zjjbmit34dyx">
                      Upload any Images or Tell us what you are Looking for
                    </p>
                  </div>
                  <ChevronRightIcon className="w-6 h-6 text-gray-300 group-hover:text-[#1E2B55] group-hover:translate-x-1 transition-all" data-id="element-7454" data-mp="j7w64kh6or5m" />
                </button>
              </div>
            </div>
          </section>

          {/* 5. Direct Access to Medical Advice */}
          <section id="medical-advice" className="py-20 bg-[#1E2B55] relative overflow-hidden" data-id="element-7455" data-mp="1g8gwkv81xt">
            <div className="absolute inset-0 opacity-5" data-id="element-7456" data-mp="3mgithdu1ij30">
              <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
              backgroundSize: '40px 40px'
            }} data-id="element-7457" data-mp="3mfprsr9xujrq"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10 max-w-4xl text-center" data-id="element-7458" data-mp="38hj7yf1tqkb2">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4" data-id="element-7459" data-mp="33ioln34nedm7">
                Need Medical Advice?
              </h2>
              <p className="text-white/90 text-base md:text-lg mb-12 max-w-2xl mx-auto" data-id="element-7460" data-mp="16nadoq22qw26">
                Our licensed pharmacists are available 24/7 to help you with
                your prescription and health concerns.
              </p>

              <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-6 max-w-3xl mx-auto" data-id="element-7461" data-mp="1e8cj74nkhwkz">
                <button className="flex-1 flex items-center justify-center md:justify-start gap-4 bg-white hover:bg-gray-50 text-[#1E2B55] py-4 px-6 md:py-5 md:px-8 rounded-3xl transition-all shadow-xl group" data-id="element-7462" data-mp="1izcj8irzgurp">
                  <div className="bg-[#f0f4f8] w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" data-id="element-7463" data-mp="qbys51ayutfi">
                    <PhoneIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-7464" data-mp="vajryzmb3b3f" />
                  </div>
                  <div className="text-left" data-id="element-7465" data-mp="108bhxo3e4n4z">
                    <div className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-gray-500 mb-0.5" data-id="element-7466" data-mp="14aq4oja6k71j">
                      Call Us Now
                    </div>
                    <div className="text-lg md:text-xl font-black text-[#1E2B55]" data-id="element-7467" data-mp="2bqcm2qekvshs">
                      800-INNOVA
                    </div>
                  </div>
                </button>

                <button onClick={() => navigate('/doctor-consultation')} className="flex-1 flex items-center justify-center md:justify-start gap-4 bg-white hover:bg-gray-50 text-[#1E2B55] py-4 px-6 md:py-5 md:px-8 rounded-3xl transition-all shadow-xl group" data-id="element-7468" data-mp="2e8y765q8pogb">
                  <div className="bg-[#f0f4f8] w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" data-id="element-7469" data-mp="2j9vm89fye2dl">
                    <StethoscopeIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-7470" data-mp="2s8h74b8wdthj" />
                  </div>
                  <div className="text-left" data-id="element-7471" data-mp="36xdioia5xomw">
                    <div className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-gray-500 mb-0.5" data-id="element-7472" data-mp="36w27hxee1elr">
                      Live Chat
                    </div>
                    <div className="text-lg md:text-xl font-black text-[#1E2B55]" data-id="element-7473" data-mp="3fxa3u9xcg5d7">
                      Get Free Doctor Consultation
                    </div>
                  </div>
                </button>
              </div>

              <p className="mt-10 text-blue-200/60 text-sm font-medium italic" data-id="element-7474" data-mp="3um1xn7zs6xu4">
                Our Pharmacists are available 24x7
              </p>
            </div>
          </section>
        </main>

        <div className="hidden md:block" data-id="element-7475" data-mp="3ljkdr07lmhaa">
          <Footer data-id="element-7476" data-mp="3qj5bj7b18jlf" />
        </div>
        <MobileFooterNav data-id="element-7477" data-mp="3qidpoo78m08c" />

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelect={location => setSelectedLocation(location)} data-id="element-7478" data-mp="3qhlwfk144el8" />
      </div>
    </HelmetProvider>;
}